/**
 * How the PA in the XLMOTO tent reaches the player from wherever they happen
 * to be standing.
 *
 * The third of the three placement sums, and the one that needed a file of its
 * own rather than another call into `placedMix`. `karaoke.ts` answers "how far,
 * and is the navetta's wall in the way"; `yard.ts` answers "is there a roof
 * over you". This one needs both halves of the first question answered
 * differently:
 *
 *   * The *level* falls off harder than the karaoke's, not more gently. A
 *     hundred metres of open yard is what this sound is for, and a ramp slack
 *     enough to carry that far is one that has already arrived by the time you
 *     are out of the door: the top of it belongs at the end of the parking
 *     lot, not in the middle of the yard.
 *   * The *cutoff* falls off hard, and with distance rather than only with a
 *     wall. That is the difference between a PA and a rig in a room: what
 *     reaches you across a field is the bass line, not a quiet copy of the
 *     music. Air eats the top; so does a wall; neither does much to a kick
 *     drum.
 *   * And there is a third term neither of the others has: *where the stacks
 *     are pointed*. A cabinet throws forward, so the yard in front of the
 *     tent's open wall and the yard behind it are two different rooms. It
 *     scales both the level and the top, because the back of a speaker loses
 *     the top first.
 *
 * The wall term is `indoors()` rather than `insideVenue()`, because this rig is
 * outdoors: every roof in the level is between it and somebody, not just the
 * navetta's. See `YARD.roofs`.
 *
 * Pure, for the same reason `karaoke.ts`, `yard.ts` and `pace.ts` are: the
 * interesting part is the arithmetic and geometry is worth testing without a
 * browser.
 *
 * Presentation only. Nothing here is reachable from `src/sim`.
 */

import { TELTTA_DJ } from '../config/audio';
import { clamp } from '../utils/math';
import type { ListenerMix } from './karaoke';
import { indoors } from './yard';

/** Distance from the tent, in metres, ignoring height. */
export function telttaDistance(x: number, z: number): number {
  return Math.hypot(x - TELTTA_DJ.source.x, z - TELTTA_DJ.source.z);
}

/**
 * How much of the way in you are, 0 at `silent` and 1 from `full` on. Both the
 * level and the cutoff are read off this one number, which is what keeps them
 * from disagreeing about where the tent is.
 */
function reach(distance: number): number {
  const { full, silent } = TELTTA_DJ;
  return clamp((silent - distance) / (silent - full), 0, 1);
}

/**
 * How far down the throw the listener is, 0..1: 1 straight out in front of the
 * cabinets, `TELTTA_DJ.back` directly behind them, and a broad curve between.
 *
 * This is the term that gives the run its shape. Everything from the bed to
 * the tent is in front of the stacks and grows the whole way; the moment the
 * line turns past the tent's corner for the ansalanka it is round the back of
 * them, and what is left for the rest of the yard is a thump behind you - which
 * is what leaves room for a man shouting out of a hot tub.
 *
 * Right on top of the source the direction is meaningless and the arithmetic is
 * a divide by zero, so it is answered as full-on: you are standing in the tent.
 */
function aim(x: number, z: number, distance: number): number {
  if (distance < 1e-4) return 1;
  const facing =
    ((x - TELTTA_DJ.source.x) * TELTTA_DJ.axis.x +
      (z - TELTTA_DJ.source.z) * TELTTA_DJ.axis.z) /
    distance;
  // -1..1 mapped onto 0..1 and shaped, so the falloff is a broad cardioid
  // rather than a hard front-or-back switch. A cabinet has a side as well as a
  // front, and running past one is spent almost entirely in it.
  const half = Math.pow(clamp((facing + 1) / 2, 0, 1), TELTTA_DJ.aimFocus);
  return TELTTA_DJ.back + (1 - TELTTA_DJ.back) * half;
}

export function telttaMix(x: number, z: number): ListenerMix {
  const distance = telttaDistance(x, z);
  const carry = reach(distance);
  const throwAt = aim(x, z, distance);

  // One law, unlike the karaoke's two. That one takes the quieter of a ramp
  // and an inverse-distance law, because it needs a near field inside a room.
  // This one has no room to have a near field in, and an inverse law across
  // open ground is unbounded at the source and never actually reaches zero at
  // the far end - two things a ramp gets right for free. So the ramp runs the
  // whole hundred and fifty metres on its own, with `TELTTA_DJ.rolloff` bent
  // steep enough that it spends most of its travel where the run does, and
  // `full` is a plateau rather than a crossover: it is the end of the parking
  // lot, and nothing past it is any louder for being closer.
  const level = Math.pow(carry, TELTTA_DJ.rolloff) * throwAt;

  // Geometric rather than linear, because the ear reads frequency in ratios: a
  // straight lerp from 130 Hz to 13 kHz is above 6 kHz for the whole near half
  // of the yard, which is a sound that is already open before you can see what
  // is making it. Squared on top of that, so the top comes back in over the
  // last stretch rather than all the way along - which is what makes crossing
  // the parking lot the moment the thump turns into music.
  //
  // The throw is a second exponent on the same ratio rather than a multiplier
  // on the answer, for the same reason the interpolation is geometric at all:
  // halving a corner frequency is what "duller" means to an ear, and behind the
  // cabinets there is nothing left above the kick however close you are
  // standing.
  const open =
    TELTTA_DJ.farCutoff *
    Math.pow(TELTTA_DJ.nearCutoff / TELTTA_DJ.farCutoff, carry * carry * throwAt);

  // The wall scales the level and *caps* the top rather than scaling it. A
  // ceiling is what a wall actually is: it cannot make the far end of the yard
  // brighter than the distance already left it, and it can flatten the tent's
  // own apron to a kick drum when there is a navetta between the two.
  const wall = indoors(x, z) ? TELTTA_DJ.inside : TELTTA_DJ.outside;
  return { gain: level * wall.gain, cutoff: Math.min(open, wall.cutoff) };
}

/**
 * What the mixer plays before the game has said where anybody is standing.
 *
 * Silent, like the karaoke's: the run starts indoors and most of a level away,
 * and a default that is too loud is a PA at full blast in a bedroom for one
 * frame. It is corrected on the first rendered frame either way.
 */
export const TELTTA_SILENT_MIX: ListenerMix = { gain: 0, cutoff: TELTTA_DJ.farCutoff };
